import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FetchPolicy } from 'react-relay';

import { COUNT_RECORD_QUERY } from '../const';
import type { ScheduleItemsQuery$variables } from '../graphql/__generated__/ScheduleItemsQuery.graphql';
import { ScheduleItemConnectionFragment } from '../graphql/fragments/ScheduleItemConnectionFragment';
import { ScheduleItemFragment } from '../graphql/fragments/ScheduleItemFragment';
import { ScheduleItemListFragment } from '../graphql/fragments/ScheduleItemListFragment';
import { ScheduleItemsQuery } from '../graphql/ScheduleItemsQuery';
import useAccess from '../hooks/useAccess';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { SmxPermissionContext } from '../providers/permissionProvider';
import { ComponentsDispatch, RootState, store } from '../store';
import { setterGantLoader } from '../store/slices/gantLoaderSlice';
import { setterGant } from '../store/slices/gantSlice';
import { changeUpdateScheduleFetchKey } from '../store/slices/settingSlice';
import DataLoader from '../table/DataLoader';
import { ItemTaskType, ScheduleItemType, SettingGantType } from '../types';

interface IGantLoadScheduleItem {
    isActiveScheduleFilter?: boolean;
}

let prevFetchKey = Number.MIN_VALUE;

const GantLoadScheduleItem: React.FC<IGantLoadScheduleItem> = ({ isActiveScheduleFilter }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const { userId } = useContext(SmxPermissionContext);
    const settingGant = useSelector<RootState, SettingGantType | null>((state) => state.gant.settingGant);
    const mappingTaskByProject = useSelector<RootState, { [key: string]: ItemTaskType[] }>(
        (state) => state.gantLoader.mappingTaskByProject,
    );

    const filterFlexibleScheduleItem = [];

    filterProject?.length && filterFlexibleScheduleItem.push(`{"or":[{"project_id":"${filterProject.join('|')}"}]}`);
    const accessAll = useAccess(['work-item.all.view']);

    if (isActiveScheduleFilter && !accessAll) {
        const accessOwner = useAccess(['work-item.owner']);
        const accessAssignee = useAccess(['work-item.assignee']);

        const filterScheduleItemOwnerAssignee = [];
        accessOwner && filterScheduleItemOwnerAssignee.push(`{"owner_item_id":"${userId}"}`);
        accessAssignee && filterScheduleItemOwnerAssignee.push(`{"assignee":"*${userId}"}`);

        (accessAssignee || accessOwner) &&
            filterFlexibleScheduleItem.push(`{"or":[${filterScheduleItemOwnerAssignee.join(',')}]}`);
    }

    const filterFlexible = filterFlexibleScheduleItem?.length ? `[${filterFlexibleScheduleItem.join(',')}]` : undefined;

    const updateScheduleItemFetchKey = useSelector<RootState, number>(
        (state) => state.setting.updateScheduleItemFetchKey,
    );

    const defaultFetchPolicy: FetchPolicy = 'store-and-network';

    const fetchPolicy =
        prevFetchKey !== updateScheduleItemFetchKey
            ? (store.getState().setting.fetchPolicy ?? defaultFetchPolicy)
            : defaultFetchPolicy;

    const prefixFetchKey =
        fetchPolicy === 'network-only'
            ? `${settingGant?.data.gantVersion}-${new Date().getTime()}`
            : `${settingGant?.data.gantVersion}`;

    prevFetchKey = updateScheduleItemFetchKey;

    useEffect(() => {
        dispatch(changeUpdateScheduleFetchKey());
    }, [filterProject]);

    return (
        <DataLoader<ScheduleItemsQuery$variables, {}>
            query={ScheduleItemsQuery}
            fragmentList={ScheduleItemListFragment}
            fragment={[ScheduleItemFragment]}
            fragmentConnection={ScheduleItemConnectionFragment}
            nameQuery={'scheduleItems'}
            varRequest={{
                first: COUNT_RECORD_QUERY,
                filter_flexible: filterFlexible,
            }}
            prefixFetchKey={prefixFetchKey}
            fetchPolicy={fetchPolicy}
            onUpdateData={(rows: any[], count: number, connectionId: string) => {
                const newRows: ScheduleItemType[] = rows;

                dispatch(
                    setterGantLoader({
                        dataScheduleItemFromServer: {
                            rows: newRows.map((item) => ({
                                ...item,
                                tasks: mappingTaskByProject[item.id] || [],
                            })),
                            count,
                            connectionId,
                        },
                    }),
                );

                dispatch(
                    setterGant({
                        connectionItemsId: connectionId,
                    }),
                );
            }}
            externalFetchKey={updateScheduleItemFetchKey}
        />
    );
};

export default GantLoadScheduleItem;
